/**
 * Driver standards + emergency contacts (254g): pure helpers shared by the driver profile step,
 * the listing summary, the rider booking step and Account. Settings decide what is asked
 * (see `driverStandards` in ./settings); the API re-checks everything.
 */
import type {
  RidesDriverProfileView,
  RidesEmergencyContactView,
  RidesUpsertDriverProfileRequest,
} from '@actschurch/ccms-sdk';
import type { RequirementLevel } from './settings';

// ---------------------------------------------------------------- emergency contact

export interface EmergencyDraft {
  name: string;
  phone: string;
  relationship: string;
}

export const EMPTY_EMERGENCY: EmergencyDraft = { name: '', phone: '', relationship: '' };

export function emergencyFromView(v: RidesEmergencyContactView | null | undefined): EmergencyDraft {
  return { name: v?.name?.trim() ?? '', phone: v?.phone?.trim() ?? '', relationship: v?.relationship?.trim() ?? '' };
}

const digits = (phone: string) => phone.replace(/\D/g, '').length;

/** The API accepts 7–20 digits (spaces, +, brackets and dashes are fine). */
export function emergencyPhoneOk(phone: string): boolean {
  const n = digits(phone);
  return n >= 7 && n <= 20;
}

export function emergencyIsBlank(d: EmergencyDraft): boolean {
  return !d.name.trim() && !d.phone.trim() && !d.relationship.trim();
}

/** A contact the API will keep: a name and a callable number. */
export function emergencyComplete(d: EmergencyDraft | RidesEmergencyContactView | null | undefined): boolean {
  return !!d && (d.name ?? '').trim().length >= 2 && emergencyPhoneOk(d.phone ?? '');
}

/** Optional: blank or complete. Required: complete. Not asked: always fine. */
export function emergencyValid(d: EmergencyDraft, level: RequirementLevel): boolean {
  if (level === 'notAsked') return true;
  if (level === 'optional' && emergencyIsBlank(d)) return true;
  return emergencyComplete(d);
}

/** Field-level messages (only for what the member has started typing, unless `force`). */
export function emergencyErrors(d: EmergencyDraft, level: RequirementLevel, force = false): { name?: string; phone?: string } {
  const out: { name?: string; phone?: string } = {};
  const started = !emergencyIsBlank(d);
  if (!started && !(force && level === 'required')) return out;
  if (d.name.trim().length < 2) out.name = 'Please add their name.';
  if (!d.phone.trim()) out.phone = 'Please add a phone number we can call.';
  else if (!emergencyPhoneOk(d.phone)) out.phone = 'Please enter a phone number with 7 to 20 digits.';
  return out;
}

/** Request body: empty name + phone clears an optional contact (the API keeps a required one). */
export function emergencyRequest(d: EmergencyDraft): RidesEmergencyContactView {
  return { name: d.name.trim(), phone: d.phone.trim(), relationship: d.relationship.trim() || null };
}

export function emergencyChanged(d: EmergencyDraft, saved: RidesEmergencyContactView | null | undefined): boolean {
  const s = emergencyFromView(saved);
  return d.name.trim() !== s.name || d.phone.trim() !== s.phone || d.relationship.trim() !== s.relationship;
}

// ---------------------------------------------------------------- licence experience

export const FIRST_LICENCE_YEAR_MIN = 1950;

export function currentYear(): number {
  return new Date().getFullYear();
}

/** MinimumYearsLicensed == 0, or a year in range that gives enough experience. */
export function experienceOk(year: number | null | undefined, minYears: number): boolean {
  if (!minYears || minYears <= 0) return true;
  if (!year) return false;
  const now = currentYear();
  return year >= FIRST_LICENCE_YEAR_MIN && year <= now && now - year >= minYears;
}

// ---------------------------------------------------------------- declaration

/** Accepted for the CURRENT text version (a cached profile may predate a version bump). */
export function declarationAcceptedFor(d: RidesDriverProfileView | null | undefined, version: string): boolean {
  if (!d?.declarationAccepted) return false;
  return !d.declarationTextVersion || d.declarationTextVersion === version;
}

// ---------------------------------------------------------------- PUT me/driver from the view

/**
 * A full PUT me/driver body rebuilt from the saved profile, plus `patch` — used to save the
 * declaration / licence year / emergency contact from the listing summary without re-asking for the
 * vehicle and licence. Data-processing consent is omitted (null = unchanged for an existing member).
 */
export function driverUpsertFromView(
  d: RidesDriverProfileView,
  patch: Partial<RidesUpsertDriverProfileRequest> = {},
): RidesUpsertDriverProfileRequest {
  return {
    sexID: d.sexID,
    displayName: d.displayName,
    vehicle: { make: d.vehicle.make, model: d.vehicle.model, colour: d.vehicle.colour, plate: d.vehicle.plate, seats: d.vehicle.seats },
    licence: {
      licenceTypeID: d.licence.licenceTypeID,
      licenceCodeID: d.licence.licenceCodeID ?? null,
      country: d.licence.country,
      expiresAt: d.licence.expiresAt ?? null,
      inEnglish: d.licence.inEnglish,
      hasIdp: d.licence.hasIdp,
    },
    ...patch,
  };
}
